import { Component, memo, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { useSelector } from '@xstate/store/react';
import { ALL_CONNECTOR_SIDES } from '../../types';
import { MIN_BLOCKNODE_WIDTH, MIN_BLOCKNODE_HEIGHT } from '../../entityTypes';
import type { CanvasEntity } from '../../entityTypes';
import { getBlockNodeData } from '../../entityTypes';
import { useCanvasStores } from '../../stores/CanvasContext';
import { useMergedDropTarget } from '../../hooks/useMergedDropTarget';
import { useLockedLayerIds } from '../../hooks/useLockedLayerIds';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { useEntityData } from '../../stores/EntityDataContext';
import { CanvasConnector } from '../../components/CanvasConnector';
import { CanvasResizeHandles } from '../../components/CanvasResizeHandles';
import { BlockNoteEditor } from '@blocknote/core';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/core/style.css';
import '@blocknote/mantine/style.css';

// Singleton editor for HTML conversion — uses DOMSerializer only (no NodeViews).
let _htmlConverter: BlockNoteEditor | null = null;
function getHtmlConverter(): BlockNoteEditor {
  if (!_htmlConverter) {
    _htmlConverter = BlockNoteEditor.create();
  }
  return _htmlConverter;
}

// Error boundary to catch BlockNote rendering errors gracefully.
class BlockNoteErrorBoundary extends Component<
  { children: ReactNode; onClear?: () => void; errorLabel?: string; clearLabel?: string },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error) {
    console.warn('BlockNote render error caught:', error.message);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center gap-1 w-full h-full p-2 text-xs">
          <span className="text-red-400 italic">{this.props.errorLabel ?? 'Inhalt fehlerhaft'}</span>
          {this.props.onClear && (
            <button
              className="px-2 py-0.5 rounded bg-red-100 hover:bg-red-200 text-red-600 cursor-pointer"
              onClick={() => {
                this.props.onClear!();
                this.setState({ hasError: false });
              }}
            >
              {this.props.clearLabel ?? 'Inhalt leeren'}
            </button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export interface CanvasBlockNodeProps {
  entity: CanvasEntity;
}

function CanvasBlockNodeInner({ entity }: CanvasBlockNodeProps) {
  const { t } = useTranslation();
  const { store } = useCanvasStores();
  const { mergedDropTargetId, mergedDropTargetSide } = useMergedDropTarget();
  const { selectedEdgeConnectors, connectorHasEdge } = useEntityData();
  const lockedLayerIds = useLockedLayerIds();
  const d = getBlockNodeData(entity);

  const isSelected = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds.includes(entity.id));
  const isEditing = useSelector(canvasSelectionStore, s => s.context.editingElementId === entity.id);

  const isLocked = lockedLayerIds.has(entity.layerId);
  const isDropTarget = mergedDropTargetId === entity.id;
  const dropTargetSide = isDropTarget ? mergedDropTargetSide : null;

  const handleResize = useCallback((x: number, y: number, width: number, height: number) => {
    store.send({ type: 'entity.resize', entityId: entity.id, x, y, width, height });
  }, [store, entity.id]);

  const handleClearContent = useCallback(() => {
    store.send({
      type: 'blocknode.updateData',
      entityId: entity.id,
      dataChanges: { content: [] },
    });
  }, [store, entity.id]);

  return (
    <div
      data-canvas-entity
      data-canvas-blocknode={entity.id}
      data-entity-id={entity.id}
      data-entity-kind={entity.kind}
      style={{
        position: 'absolute',
        left: entity.x,
        top: entity.y,
        width: entity.width,
        height: entity.height,
      }}
      className={`select-none ${isSelected ? 'ring-2 ring-blue-400' : ''} ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      <style>{`
[data-canvas-entity] .bn-editor { padding-inline: 4px !important; }
[data-canvas-entity] .bn-side-menu .mantine-UnstyledButton-root:not(.mantine-Menu-item) {
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
      `}</style>
      <div
        className="w-full h-full rounded-md overflow-hidden cursor-grab active:cursor-grabbing"
        style={{
          backgroundColor: d.backgroundColor,
          border: d.borderColor
            ? `${d.borderWidth ?? 1}px solid ${d.borderColor}`
            : '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <BlockNoteErrorBoundary onClear={handleClearContent} errorLabel={t('canvas.contentError')} clearLabel={t('canvas.clearContent')}>
          {isEditing ? (
            <BlockNodeEditor entity={entity} />
          ) : (
            <BlockNodeReadOnly content={d.content} />
          )}
        </BlockNoteErrorBoundary>
      </div>

      {/* Resize handles when selected */}
      {isSelected && !isLocked && (
        <CanvasResizeHandles
          x={entity.x}
          y={entity.y}
          width={entity.width}
          height={entity.height}
          minWidth={MIN_BLOCKNODE_WIDTH}
          minHeight={MIN_BLOCKNODE_HEIGHT}
          onResize={handleResize}
        />
      )}

      {/* Connectors */}
      {entity.connectable && ALL_CONNECTOR_SIDES.map(side => {
        const key = `${entity.id}:${side}`;
        return (
          <CanvasConnector
            key={side}
            taskId={entity.id}
            entityWidth={entity.width}
            entityHeight={entity.height}
            side={side}
            isDropTarget={isDropTarget}
            dropTargetSide={dropTargetSide}
            isEdgeEndpoint={selectedEdgeConnectors.has(key)}
            hasEdge={connectorHasEdge.has(key)}
          />
        );
      })}
    </div>
  );
}

// ============================================
// Read-Only Renderer — blocksToFullHTML for pixel-perfect editor output.
// Errors are caught by try-catch + ErrorBoundary.
// ============================================

function BlockNodeReadOnly({ content }: { content: unknown[] }) {
  const { t } = useTranslation();
  if (!content || content.length === 0) {
    return <div className="text-gray-400 text-sm italic">{t('task.doubleClickToEdit')}</div>;
  }

  const html = useMemo(() => {
    try {
      return getHtmlConverter().blocksToFullHTML(content as any);
    } catch {
      return `<em>${t('canvas.renderError')}</em>`;
    }
  }, [content, t]);

  return (
    <div className="bn-container w-full h-full overflow-hidden" data-color-scheme="light">
      <div
        className="bn-editor bn-default-styles"
        style={{ paddingInline: '4px' }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

// ============================================
// Inline BlockNote Editor
// ============================================

function BlockNodeEditor({ entity }: { entity: CanvasEntity }) {
  const { store } = useCanvasStores();
  const d = getBlockNodeData(entity);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const editor = useCreateBlockNote({
    initialContent: d.content && d.content.length > 0
      ? (d.content as any)
      : undefined,
  });

  const handleSaveAndStop = useCallback(() => {
    store.send({
      type: 'blocknode.updateData',
      entityId: entity.id,
      dataChanges: { content: editor.document },
    });
    canvasSelectionStore.send({ type: 'editing.stop' });
  }, [store, entity.id, editor]);

  // Native wheel listener: stops wheel events from reaching the canvas container
  // (which calls preventDefault on all wheel events for pan/zoom).
  // Must be native because the canvas handler is also native and fires during bubble
  // before React's synthetic handlers reach the root.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      // Let Ctrl/Cmd+wheel through for canvas zoom
      if (e.ctrlKey || e.metaKey) return;
      e.stopPropagation();
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        canvasSelectionStore.send({ type: 'editing.stop' });
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);


  return (
    <div
      ref={wrapperRef}
      className="w-full h-full overflow-auto"
      onPointerDown={e => e.stopPropagation()}
      onBlur={(e) => {
        // Only trigger save if focus leaves this container entirely
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          handleSaveAndStop();
        }
      }}
    >
      <BlockNoteView editor={editor} theme="light" />
    </div>
  );
}

export const CanvasBlockNode = memo(CanvasBlockNodeInner);
